import React, { useState } from 'react';
import EmployeePermissionView from './EmployeePermissionView';
import PermissionDiagnostics from './PermissionDiagnostics';
import PermissionItemView from './PermissionItemView';
import TenantModuleView from './TenantModuleView';
import CompanyAddPanel from './CompanyAddPanel';
import ConfidentialLockPanel from '../confidential/ConfidentialLockPanel';
import { useAssignmentOverview } from './useAssignmentOverview';
import { usePermissionAssignment } from './usePermissionAssignment';
import { isAdminUser } from '../adminlayout/sidebarFilters';
import { emptyPageStyle } from './styles';
import {
  summaryCardStyle,
  summaryGridStyle,
  summaryLabelStyle,
  summaryValueStyle,
} from './dashboardStyles';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdTabGroup, stdTabBtn,
} from '../adminlayout/designStandard';

const ADMIN_VIEW_TABS = [
  { key: 'employee', label: '직원별 보기' },
  { key: 'item', label: '업무·기능별 보기' },
  { key: 'tenantModules', label: '고객사 기능 배정' },
  { key: 'diagnostics', label: '권한 점검' },
];

const DELEGATE_VIEW_TABS = ADMIN_VIEW_TABS.filter((tab) => tab.key !== 'tenantModules');

export default function PermissionAssignment({ isDelegate = false, onPermissionsChanged }) {
  const [viewMode, setViewMode] = useState('employee');
  // 운영자(admin)=true / 테넌트 관리자(employee+TENANT_ADMIN)=false.
  // 회사 추가는 테넌트 관리자 전용(운영자는 "테넌트 관리"에서 추가). 위임자에게도 노출 안 함.
  const isOperator = isAdminUser();
  const {
    isMobile,
    savingUserId,
    grantAssignment,
    revokeAssignment,
  } = usePermissionAssignment({ isDelegate, onPermissionsChanged });
  const { overview, overviewLoading, refreshOverview } = useAssignmentOverview({ isDelegate });
  const hasOverview = Boolean(overview);
  const initialLoading = overviewLoading && !hasOverview;
  const refreshing = overviewLoading && hasOverview;
  const viewTabs = isDelegate ? DELEGATE_VIEW_TABS : ADMIN_VIEW_TABS;

  async function handleGrant(spec) {
    const ok = await grantAssignment(spec);
    if (ok) await refreshOverview();
  }

  async function handleRevoke(spec) {
    const ok = await revokeAssignment(spec);
    if (ok) await refreshOverview();
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* ── 페이지 헤더: 직원 관리 표준(텍스트 제목 + 부제) ──
          위임자(/my) 화면도 같은 모양으로 통일했다(2026-09-23 사용자 요청). 예전에는 위임자만
          흰 카드 박스라 같은 메뉴가 경로에 따라 달라 보였다. 설명 문구만 보는 사람에 맞춘다. */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>업무/기능 배정</h2>
          <div style={stdPageSubtitle}>
            {isDelegate
              ? '본인이 배정받은 업무와 기능 범위 안에서만 다른 직원에게 배정할 수 있습니다.'
              : '직원별 업무·기능 배정을 관리합니다.'}
          </div>
        </div>
      </div>

      {!isOperator && !isDelegate && (
        <CompanyAddPanel onAdded={refreshOverview} />
      )}
      {/* 회사별 대외비 비밀번호(2026-10-16, 급여·연차정산·흐름) — 테넌트 관리자만. 대외비 메뉴를 쓰는 회사가 없으면 칸이 안 보인다 */}
      {!isOperator && !isDelegate && <ConfidentialLockPanel />}

      <div style={{ ...stdTabGroup, maxWidth: '100%', overflowX: 'auto' }}>
        {viewTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            style={stdTabBtn(viewMode === tab.key)}
            onClick={() => setViewMode(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={summaryGridStyle}>
        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>직원</div>
          <div style={summaryValueStyle}>{overview?.stats?.employeeCount ?? '-'}</div>
        </div>
        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>업무</div>
          <div style={summaryValueStyle}>{overview?.stats?.taskCount ?? '-'}</div>
        </div>
        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>기능</div>
          <div style={summaryValueStyle}>{overview?.stats?.featureCount ?? '-'}</div>
        </div>
        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>점검 필요</div>
          <div style={summaryValueStyle}>{overview?.stats?.issueCount ?? '-'}</div>
        </div>
      </div>

      {initialLoading && <div style={emptyPageStyle}>업무/기능 배정 현황을 불러오는 중입니다.</div>}
      {refreshing && (
        <div style={{ ...emptyPageStyle, padding: 8 }}>
          변경 내용을 반영하는 중입니다.
        </div>
      )}

      {hasOverview && viewMode === 'employee' && (
        <EmployeePermissionView
          overview={overview}
          isMobile={isMobile}
          savingUserId={savingUserId}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
        />
      )}

      {hasOverview && viewMode === 'item' && (
        <PermissionItemView
          overview={overview}
          isMobile={isMobile}
          savingUserId={savingUserId}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
        />
      )}

      {hasOverview && viewMode === 'tenantModules' && (
        <TenantModuleView overview={overview} onRefresh={refreshOverview} />
      )}

      {hasOverview && viewMode === 'diagnostics' && (
        <PermissionDiagnostics overview={overview} />
      )}
    </div>
  );
}
